import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, onSnapshot, addDoc, doc, writeBatch, updateDoc } from 'firebase/firestore';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button } from '../components/ui/button';
import { DotsSixVertical, Copy, Plus, Minus, CheckCircle } from '@phosphor-icons/react';

const QuestionItem = ({ id, index, question, answer, moveQuestion, isSelected, onSelect, isMultipleChoice, answerOptions, correctAnswers }) => {
  const [, ref] = useDrag({
    type: 'QUESTION',
    item: { id, index },
  });

  const [, drop] = useDrop({
    accept: 'QUESTION',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveQuestion(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <li
      ref={(node) => ref(drop(node))}
      className={`border border-solid border-[#cccccc] rounded-lg p-4 bg-[#f2f2ff] transition-colors duration-200 hover:bg-[#e6e6ff] ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onClick={() => onSelect(id)}
    >
      <div className="flex justify-between items-start">
        <div className="flex-grow">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center bg-primary text-white rounded-full text-sm font-medium">
              {index + 1}
            </span>
            <div className="w-full">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-base font-semibold text-foreground">{question}</p>
                <span className={`text-xs px-2 py-1 rounded-full min-w-[50px] inline-block text-center ${isMultipleChoice
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-green-100 text-green-800'
                  }`}>
                  {isMultipleChoice ? '선다형' : '단답형'}
                </span>
              </div>
              {isMultipleChoice ? (
                <div className="space-y-2">
                  {answerOptions.map((option, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-xl transition-all duration-200">
                      <div className={`flex items-center gap-3 ${correctAnswers.includes(idx)
                        ? 'text-blue-800'
                        : 'text-gray-700'
                        }`}>
                        <div className={`w-6 h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${correctAnswers.includes(idx)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                          }`}>
                          {correctAnswers.includes(idx) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className={`${correctAnswers.includes(idx)
                          ? 'font-medium'
                          : ''
                          }`}>
                          {option}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-lg text-muted-foreground">{answer}</p>
              )}
            </div>
          </div>
        </div>
        <div className="cursor-move text-gray-400 hover:text-gray-500 ml-2">
          <DotsSixVertical size={18} />
        </div>
      </div>
    </li>
  );
};

function QuizDashboard() {
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [isMultipleChoice, setIsMultipleChoice] = useState(false);
  const [answerOptions, setAnswerOptions] = useState(['']);
  const [selectedAnswers, setSelectedAnswers] = useState([0]);
  const [isLoading, setIsLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editAnswerOptions, setEditAnswerOptions] = useState(['']);
  const [editSelectedAnswers, setEditSelectedAnswers] = useState([0]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'quizzes', quizId), (doc) => {
      if (doc.exists()) {
        const quizData = { id: doc.id, ...doc.data() };
        setQuiz(quizData);
        setEditedTitle(quizData.name);
      }
    });

    const questionsUnsubscribe = onSnapshot(collection(db, `quizzes/${quizId}/questions`), (snapshot) => {
      const questionData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setQuestions(questionData.sort((a, b) => (a.order || 0) - (b.order || 0)));
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
      questionsUnsubscribe();
    };
  }, [quizId]);

  const addQuestion = async (e) => {
    e.preventDefault();

    // 문제 내용이 비어있는지 확인
    if (!newQuestion.trim()) return;

    // 선다형 모드일 때의 유효성 검사
    if (isMultipleChoice) {
      // 모든 보기가 비어있지 않은지 확인
      if (answerOptions.some(option => !option.trim())) {
        alert('모든 보기를 입력해주세요.');
        return;
      }
      // 정답이 선택되었는지 확인
      if (selectedAnswers.length === 0) {
        alert('정답을 하나 이상 선택해주세요.');
        return;
      }
    } else {
      // 단답형 모드일 때의 유효성 검사
      if (!newAnswer.trim()) return;
    }

    await addDoc(collection(db, `quizzes/${quizId}/questions`), {
      question: newQuestion,
      answer: newAnswer,
      isMultipleChoice,
      answerOptions: isMultipleChoice ? answerOptions : [newAnswer],
      correctAnswers: isMultipleChoice ? selectedAnswers : [0],
      order: questions.length
    });

    setNewQuestion('');
    setNewAnswer('');
    setAnswerOptions(['']);
    setSelectedAnswers([0]);
  };

  const addAnswerOption = () => {
    setAnswerOptions([...answerOptions, '']);
  };

  const removeAnswerOption = (index) => {
    const newOptions = answerOptions.filter((_, i) => i !== index);
    setAnswerOptions(newOptions);
    setSelectedAnswers(selectedAnswers.filter(i => i !== index));
  };

  const handleAnswerOptionChange = (index, value) => {
    const newOptions = [...answerOptions];
    newOptions[index] = value;
    setAnswerOptions(newOptions);
  };

  const toggleAnswerSelection = (index) => {
    if (selectedAnswers.includes(index)) {
      setSelectedAnswers(selectedAnswers.filter(i => i !== index));
    } else {
      setSelectedAnswers([...selectedAnswers, index]);
    }
  };

  const moveQuestion = useCallback((dragIndex, hoverIndex) => {
    const draggedQuestion = questions[dragIndex];
    const newQuestions = [...questions];
    newQuestions.splice(dragIndex, 1);
    newQuestions.splice(hoverIndex, 0, draggedQuestion);

    const updatedQuestions = newQuestions.map((q, index) => ({ ...q, order: index }));

    setQuestions(updatedQuestions);
  }, [questions]);

  const saveQuizState = useCallback(async () => {
    const batch = writeBatch(db);

    // 퀴즈 문서 업데이트
    const quizRef = doc(db, `quizzes/${quizId}`);
    batch.update(quizRef, {
      name: editedTitle,
      lastUpdated: new Date(),
      questionCount: questions.length
    });

    // 각 질문 업데이트
    questions.forEach((question, index) => {
      const questionRef = doc(db, `quizzes/${quizId}/questions/${question.id}`);
      batch.update(questionRef, {
        question: question.question,
        answer: question.answer,
        isMultipleChoice: question.isMultipleChoice || false,
        answerOptions: question.answerOptions || [question.answer],
        correctAnswers: question.correctAnswers || [0],
        order: index
      });
    });

    await batch.commit();
    alert('퀴즈가 저장되었습니다. 이제 플레이할 수 있습니다.');
  }, [quizId, editedTitle, questions]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('복사 완료!');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      setCopySuccess('복사 실패. 다시 시도해주세요.');
    }
  };

  const handleSelectQuestion = (questionId) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleDeleteSelectedQuestions = async () => {
    if (window.confirm('선택한 질문들을 삭제하시겠습니까?')) {
      const batch = writeBatch(db);
      selectedQuestions.forEach((questionId) => {
        const questionRef = doc(db, `quizzes/${quizId}/questions/${questionId}`);
        batch.delete(questionRef);
      });
      await batch.commit();
      setSelectedQuestions([]);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    if (question.isMultipleChoice) {
      setEditAnswerOptions(question.answerOptions || ['']);
      setEditSelectedAnswers(question.correctAnswers || [0]);
    }
    setIsEditModalOpen(true);
  };

  const addEditAnswerOption = () => {
    setEditAnswerOptions([...editAnswerOptions, '']);
  };

  const removeEditAnswerOption = (index) => {
    const newOptions = editAnswerOptions.filter((_, i) => i !== index);
    setEditAnswerOptions(newOptions);
    setEditSelectedAnswers(editSelectedAnswers.filter(i => i !== index));
  };

  const handleEditAnswerOptionChange = (index, value) => {
    const newOptions = [...editAnswerOptions];
    newOptions[index] = value;
    setEditAnswerOptions(newOptions);
  };

  const toggleEditAnswerSelection = (index) => {
    setEditSelectedAnswers(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingQuestion) return;

    // 유효성 검사
    if (!editingQuestion.question.trim()) return;

    if (editingQuestion.isMultipleChoice) {
      // 선다형 문제 유효성 검사
      if (editAnswerOptions.some(option => !option.trim())) {
        alert('모든 보기를 입력해주세요.');
        return;
      }
      if (editSelectedAnswers.length === 0) {
        alert('정답을 하나 이상 선택해주세요.');
        return;
      }
    } else {
      // 단답형 문제 유효성 검사
      if (!editingQuestion.answer.trim()) return;
    }

    const questionRef = doc(db, `quizzes/${quizId}/questions/${editingQuestion.id}`);
    await updateDoc(questionRef, {
      question: editingQuestion.question,
      answer: editingQuestion.answer,
      isMultipleChoice: editingQuestion.isMultipleChoice,
      answerOptions: editingQuestion.isMultipleChoice ? editAnswerOptions : [editingQuestion.answer],
      correctAnswers: editingQuestion.isMultipleChoice ? editSelectedAnswers : [0],
    });

    setIsEditModalOpen(false);
    setEditingQuestion(null);
    setEditAnswerOptions(['']);
    setEditSelectedAnswers([0]);
  };

  const handleEditTitle = () => {
    setIsEditingTitle(!isEditingTitle);
    if (isEditingTitle) {
      handleSaveTitle();
    }
  };

  const handleSaveTitle = async () => {
    if (editedTitle.trim() !== '') {
      await updateDoc(doc(db, 'quizzes', quizId), { name: editedTitle });
      setIsEditingTitle(false);
    }
  };

  if (isLoading) return (
    <div className="flex justify-center items-center h-[calc(100vh-100px)]">
      <p className="text-lg font-semibold text-primary animate-pulse">로딩 중...</p>
    </div>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="container mx-auto px-4 pt-24 pb-24">
        <div className="max-w-2xl mx-auto">
          <div className="card mb-8">
            <div className="flex items-center justify-between mb-4">
              {isEditingTitle ? (
                <div className="flex items-center">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-2xl font-bold text-primary mr-2 border-b border-primary focus:outline-none"
                  />
                </div>
              ) : (
                <h1 className="text-2xl font-bold text-primary">{quiz?.name}</h1>
              )}
              <button onClick={handleEditTitle} className="text-white hover:bg-green-500 bg-green-400 rounded-md font-semibold text-xs px-2 mr-1 py-1">
                {isEditingTitle ? "저장" : "수정"}
              </button>
            </div>
            <div className="flex items-center bg-gray-100 border border-gray-200 rounded-xl py-3 px-4 mb-2">
              <span className="text-gray-600 mr-2 font-semibold">🔗 함께 만들기</span>
              <p className="text-muted-foreground mr-2 text-sm flex-grow">
                {quiz?.code ? `${window.location.origin}/create-quiz/${quiz.code}` : '로딩 중...'}
              </p>
              <button
                onClick={() => quiz?.code && copyToClipboard(`${window.location.origin}/create-quiz/${quiz.code}`)}
                className="text-muted-foreground hover:text-foreground transition-colors duration-200"
                title="링크 복사"
                disabled={!quiz?.code}
              >
                <Copy size={16} />
              </button>
            </div>
            {copySuccess && <p className="text-accent text-sm mb-4">{copySuccess}</p>}
            <span className="text-gray-600 mr-2 text-sm font-light ml-1">🎀  학생들에게 링크를 공유하여, 함께 퀴즈를 완성해보세요.</span>
          </div>

          <div className="card mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">퀴즈 문제 추가</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">단답형</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isMultipleChoice}
                    onChange={(e) => setIsMultipleChoice(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span className="text-sm text-gray-600">선다형</span>
              </div>
            </div>
            <form onSubmit={addQuestion} className="space-y-4">
              <div>
                <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">문제</label>
                <input
                  type="text"
                  id="question"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="문제를 입력하세요"
                  required
                />
              </div>
              {isMultipleChoice ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">보기</label>
                  {answerOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-3 mb-2">
                      <div
                        onClick={() => toggleAnswerSelection(index)}
                        className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 flex-1 ${selectedAnswers.includes(index)
                          ? 'bg-blue-100 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                          }`}
                      >
                        <div className={`w-6 h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${selectedAnswers.includes(index)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                          }`}>
                          {selectedAnswers.includes(index) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleAnswerOptionChange(index, e.target.value)}
                          className={`flex-1 bg-transparent border-none focus:outline-none focus:ring-0 ${selectedAnswers.includes(index)
                            ? 'text-blue-800 font-medium'
                            : 'text-gray-700'
                            }`}
                          placeholder={`보기 ${index + 1}`}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => removeAnswerOption(index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-xl transition-colors duration-200"
                          >
                            <Minus />
                          </button>
                        )}
                        {index === answerOptions.length - 1 && (
                          <button
                            type="button"
                            onClick={addAnswerOption}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-xl transition-colors duration-200"
                          >
                            <Plus />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">정답</label>
                  <input
                    type="text"
                    id="answer"
                    value={newAnswer}
                    onChange={(e) => setNewAnswer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="정답을 입력하세요"
                    required
                  />
                </div>
              )}
              <Button type="submit" className="w-full">
                <Plus size={18} className="mr-2" weight="bold" />
                문제 추가
              </Button>
            </form>
          </div>

          <div className="card bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-primary">
                질문 목록 ({questions.length}개)
              </h2>
              <div>
                {selectedQuestions.length === 1 && (
                  <button
                    onClick={() => handleEditQuestion(questions.find(q => q.id === selectedQuestions[0]))}
                    className="text-white hover:bg-green-500 bg-green-400 rounded-md font-semibold text-xs px-2 mr-1 py-1"
                    title="선택된 질문 수정"
                  >
                    수정
                  </button>
                )}
                {selectedQuestions.length > 0 && (
                  <button
                    onClick={handleDeleteSelectedQuestions}
                    className="text-white hover:bg-pink-600 bg-pink-500 rounded-md font-semibold text-xs px-2 py-1"
                    title="선택된 질문 삭제"
                  >
                    삭제
                  </button>
                )}
              </div>
            </div>
            <ul className="space-y-4 whitespace-break-spaces">
              {questions.map((question, index) => (
                <QuestionItem
                  key={question.id}
                  id={question.id}
                  index={index}
                  question={question.question}
                  answer={question.answer}
                  moveQuestion={moveQuestion}
                  isSelected={selectedQuestions.includes(question.id)}
                  onSelect={handleSelectQuestion}
                  isMultipleChoice={question.isMultipleChoice}
                  answerOptions={question.answerOptions}
                  correctAnswers={question.correctAnswers}
                />
              ))}
            </ul>
            <div className="flex justify-end mt-6">
              <button onClick={saveQuizState} className="w-full font-bold bg-black text-white px-4 py-2 rounded-xl hover:bg-gray-700">
                저장하기
              </button>
            </div>
          </div>
        </div>
      </div>

      {isEditModalOpen && editingQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-bold mb-4">문제 수정</h3>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">문제</label>
                <input
                  type="text"
                  value={editingQuestion.question}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="문제를 입력하세요"
                  required
                />
              </div>
              {editingQuestion.isMultipleChoice ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">보기</label>
                  {editAnswerOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-3 mb-2">
                      <div
                        onClick={() => toggleEditAnswerSelection(index)}
                        className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 flex-1 ${editSelectedAnswers.includes(index)
                          ? 'bg-blue-100 border-2 border-blue-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                          }`}
                      >
                        <div className={`w-6 h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${editSelectedAnswers.includes(index)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                          }`}>
                          {editSelectedAnswers.includes(index) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleEditAnswerOptionChange(index, e.target.value)}
                          className={`flex-1 bg-transparent border-none focus:outline-none focus:ring-0 ${editSelectedAnswers.includes(index)
                            ? 'text-blue-800 font-medium'
                            : 'text-gray-700'
                            }`}
                          placeholder={`보기 ${index + 1}`}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => removeEditAnswerOption(index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-xl transition-colors duration-200"
                          >
                            <Minus />
                          </button>
                        )}
                        {index === editAnswerOptions.length - 1 && (
                          <button
                            type="button"
                            onClick={addEditAnswerOption}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-xl transition-colors duration-200"
                          >
                            <Plus />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">정답</label>
                  <input
                    type="text"
                    value={editingQuestion.answer}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, answer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="정답을 입력하세요"
                    required
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingQuestion(null);
                    setEditAnswerOptions(['']);
                    setEditSelectedAnswers([0]);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DndProvider>
  );
}

export default QuizDashboard;
